import React, { useRef, useState } from "react";
import { useLazyQuery } from "@apollo/react-hooks";
import { FieldError } from "@streeteasy/design-system/form";
import { number } from "prop-types";

import { mobileAmenities } from "../../assets";
import LISTING_COUNTS from "../../queries/listing_counts";
import AmenityItem from "./components/AmenityItem";
import amenities from "./constants/amenities";
import * as S from "./styled";

const AmenitiesSection = ({ rentalsCount, salesCount }) => {
  const rentalsCountElement = useRef(null);
  const salesCountElement = useRef(null);
  const [computedRentals, setComputedRentals] = useState(rentalsCount);
  const [computedSales, setComputedSales] = useState(salesCount);
  const [selectedAmenities, setSelectedAmenities] = useState([]);
  const [requestListingCounts, { data, error, loading }] = useLazyQuery(
    LISTING_COUNTS,
    {
      variables: {
        query: `amenities:${selectedAmenities.join()}`
      },
      fetchPolicy: "network-only"
    }
  );

  const animateValue = (listing, start, end, duration) => {
    let startTimestamp = null;

    const step = timestamp => {
      if (!startTimestamp) startTimestamp = timestamp;

      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      const value = Math.floor(progress * (end - start) + start);

      if (listing === "rentals") {
        rentalsCountElement.current.innerText = value;
      } else {
        salesCountElement.current.innerText = value;
      }

      if (progress < 1) {
        window.requestAnimationFrame(step);
      }
    };

    window.requestAnimationFrame(step);
  };

  const handleAmenitySelection = ({ target }) => {
    if (selectedAmenities.includes(target.name)) {
      setSelectedAmenities(
        selectedAmenities.filter(item => item !== target.name)
      );
    } else {
      setSelectedAmenities([...selectedAmenities, target.name]);
    }

    requestListingCounts();

    if (data) {
      setComputedRentals(data.search_rentals.total_count);
      setComputedSales(data.search_sales.total_count);
    }
  };

  const amenitiesLink = listingType => {
    if (selectedAmenities.length === 0) {
      return `/${listingType}/nyc`;
    }

    return `/${listingType}/nyc/status:open|amenities:${selectedAmenities.join()}`;
  };

  const handleAnalyticsLearnMoreClick = () => {
    window.dataLayer.push({
      event: "custom_event",
      event_category: "homepage",
      event_action: "amenities_see_top_filters",
      event_label: "click"
    });
  };

  const handleAnalyticsAmenitiesSubmit = eventLabelParams => {
    window.dataLayer.push({
      event: "custom_event",
      event_category: "search",
      event_action: "submit",
      event_label: `listing_class:${eventLabelParams}`
    });
  };

  const gASelectedAmenities =
    selectedAmenities.length > 0 ? `|amenities:${selectedAmenities}` : "";

  return (
    <S.AmenitiesSectionContainer>
      <S.Container>
        <S.Title as="h2">Home search built for the big city</S.Title>
        <S.Subtitle>
          Filter by popular NYC amenities like pets allowed, elevator & doorman
        </S.Subtitle>
        <S.Link
          href="/blog/apartment-amenities-new-yorkers-want-most/"
          onClick={handleAnalyticsLearnMoreClick}
        >
          See top filters
        </S.Link>
      </S.Container>
      <S.MobileImg alt="" loading="lazy" src={mobileAmenities} />
      <S.Container>
        <S.AmenitiesList>
          {amenities.map(({ imgSrc, name, value }) => (
            <AmenityItem
              key={name}
              handleAmenitySelection={handleAmenitySelection}
              imgSrc={imgSrc}
              isChecked={selectedAmenities.includes(name)}
              name={name}
              value={value}
            />
          ))}
        </S.AmenitiesList>
        {error ? (
          <FieldError>Something went wrong! Refresh and try again</FieldError>
        ) : (
          <S.ButtonsContainer>
            <S.SeeListingsButton
              data-qa="see-rentals-button"
              href={amenitiesLink("for-rent")}
              onClick={() =>
                handleAnalyticsAmenitiesSubmit(`Rental${gASelectedAmenities}`)
              }
              target="_blank"
            >
              See&nbsp;
              <span
                ref={rentalsCountElement}
                value={data?.search_rentals?.total_count}
              >
                {data && !loading
                  ? animateValue(
                      "rentals",
                      computedRentals,
                      data?.search_rentals?.total_count,
                      1000
                    )
                  : computedRentals}
              </span>
              &nbsp;Rentals
            </S.SeeListingsButton>
            <S.SeeListingsButton
              data-qa="see-sales-button"
              href={amenitiesLink("for-sale")}
              onClick={() =>
                handleAnalyticsAmenitiesSubmit(`Sale${gASelectedAmenities}`)
              }
              target="_blank"
            >
              See&nbsp;
              <span
                ref={salesCountElement}
                value={data?.search_sales?.total_count}
              >
                {data && !loading
                  ? animateValue(
                      "sales",
                      computedSales,
                      data?.search_sales?.total_count,
                      1000
                    )
                  : computedSales}
              </span>
              &nbsp;Sales
            </S.SeeListingsButton>
          </S.ButtonsContainer>
        )}
      </S.Container>
    </S.AmenitiesSectionContainer>
  );
};

AmenitiesSection.propTypes = {
  rentalsCount: number.isRequired,
  salesCount: number.isRequired
};

export default AmenitiesSection;
